import { useParams } from "react-router-dom";
import { ActionButton, Dialog, DialogContent } from "@jhool-io/fe-components";
import { useState } from "react";
import { useGetDiagnosisCodesDisplay } from "../../../../hooks/helpers";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import {
    IAddendumDetails,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import { INTAKE_NOTE_MED_LABELS } from "../../../../utils/constants";
import {
    cn,
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import NoteContentItem from "../NoteContentItem/NoteContentItem";

import { IViewFile } from "../../../../utils/types";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import useGetDiagnosisCodeToDisplay from "../../../../modules/notes/hooks";
import styles from "../../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import CheckIcon from "../../../../icons/Check";

interface ViewIntakeNoteMedProps {
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    fieldAddendums: {
        [key: string]: IAddendumDetails[];
    } | null;
}

export default function ViewIntakeNoteMed({
    noteContent,
    fieldAddendums,
}: ViewIntakeNoteMedProps) {
    const [docInView, setDocInView] = useState<IViewFile | null>(null);

    const diagonosisCodes = useGetDiagnosisCodesDisplay(
        noteContent.diagnosis_codes
    );

    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const displayCheckedOptions = (values?: string[]) =>
        Array.isArray(values) && values.length > 0 ? (
            <div className={styles.checkboxes}>
                {values.map((value) => (
                    <div className={styles.checkbox} key={value}>
                        <span
                            className={cn(styles.checkbox_wrap, styles.checked)}
                        >
                            <CheckIcon className="stroke-text" />
                        </span>
                        {value}
                    </div>
                ))}
            </div>
        ) : (
            "--"
        );

    const displayBooleanResponse = (value?: boolean) => {
        if (value === undefined || value === null) return "--";

        return value ? "Yes" : "No";
    };

    const displayEnumValue = (value?: string) =>
        value ? removeEnumUnderscore(value) : "--";

    const displayOptionValue = (value?: string) =>
        value
            ? makeStringFirstLetterCapital(removeEnumUnderscore(value))
            : "--";

    const dateOfNextSession = noteContent.date_of_next_session
        ? formatDate(noteContent.date_of_next_session, true)
        : "--";

    return (
        <>
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.session_location}
                fieldId="session_location"
                addendums={fieldAddendums?.session_location}
                content={
                    noteContent.session_location
                        ? removeEnumUnderscore(noteContent.session_location)
                        : "--"
                }
            />
            {noteContent.session_location === SessionLocations.Telehealth && (
                <>
                    <NoteContentItem
                        headerText={
                            INTAKE_NOTE_MED_LABELS.location_of_the_client
                        }
                        fieldId="location_of_the_client"
                        addendums={fieldAddendums?.location_of_the_client}
                        content={
                            noteContent.location_of_the_client
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_client
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_client === "other" && (
                        <NoteContentItem
                            headerText={
                                INTAKE_NOTE_MED_LABELS.other_location_of_the_client
                            }
                            fieldId="other_location_of_the_client"
                            addendums={
                                fieldAddendums?.other_location_of_the_client
                            }
                            content={
                                noteContent.other_location_of_the_client || "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            INTAKE_NOTE_MED_LABELS.location_of_the_provider
                        }
                        fieldId="location_of_the_provider"
                        addendums={fieldAddendums?.location_of_the_provider}
                        content={
                            noteContent.location_of_the_provider
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_provider
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_provider === "other" && (
                        <NoteContentItem
                            headerText={
                                INTAKE_NOTE_MED_LABELS.other_location_of_the_provider
                            }
                            fieldId="other_location_of_the_provider"
                            addendums={
                                fieldAddendums?.other_location_of_the_provider
                            }
                            content={
                                noteContent.other_location_of_the_provider ||
                                "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            INTAKE_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                        }
                        fieldId="consent_was_received_for_the_teletherapy_session"
                        addendums={
                            fieldAddendums?.consent_was_received_for_the_teletherapy_session
                        }
                        content={displayBooleanResponse(
                            noteContent.consent_was_received_for_the_teletherapy_session
                        )}
                    />
                    <NoteContentItem
                        headerText={
                            INTAKE_NOTE_MED_LABELS.the_session_was_conducted_via_video
                        }
                        fieldId="the_session_was_conducted_via_video"
                        addendums={
                            fieldAddendums?.the_session_was_conducted_via_video
                        }
                        content={displayBooleanResponse(
                            noteContent.the_session_was_conducted_via_video
                        )}
                    />
                </>
            )}

            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.diagnosis_codes}
                fieldId="diagnosis_codes"
                addendums={fieldAddendums?.diagnosis_codes}
                content={
                    diagonosisCodes && Array.isArray(diagonosisCodes) ? (
                        <div className={styles.checkboxes}>
                            {diagonosisCodes.map((code) => (
                                <div className={styles.checkbox} key={code}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: true,
                                        })}
                                    >
                                        <CheckIcon className="stroke-text" />
                                    </span>
                                    {code}
                                </div>
                            ))}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.secondary_diagnosis}
                fieldId="secondary_diagnosis"
                addendums={fieldAddendums?.secondary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.secondary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.tertiary_diagnosis}
                fieldId="tertiary_diagnosis"
                addendums={fieldAddendums?.tertiary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.tertiary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.additional_diagnosis_one}
                fieldId="additional_diagnosis_one"
                addendums={fieldAddendums?.additional_diagnosis_one}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_one
                )}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.additional_diagnosis_two}
                fieldId="additional_diagnosis_two"
                addendums={fieldAddendums?.additional_diagnosis_two}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_two
                )}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.preferred_name}
                fieldId="preferred_name"
                addendums={fieldAddendums?.preferred_name}
                content={noteContent.preferred_name || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.pronoun}
                fieldId="pronoun"
                addendums={fieldAddendums?.pronoun}
                content={noteContent.pronoun || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.sex}
                fieldId="sex"
                addendums={fieldAddendums?.sex}
                content={displayOptionValue(noteContent.sex)}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.gender_identity}
                fieldId="gender_identity"
                addendums={fieldAddendums?.gender_identity}
                content={displayOptionValue(noteContent.gender_identity)}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.sexual_orientation}
                fieldId="sexual_orientation"
                addendums={fieldAddendums?.sexual_orientation}
                content={displayOptionValue(noteContent.sexual_orientation)}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.monogamous_polyamorous}
                fieldId="monogamous_polyamorous"
                addendums={fieldAddendums?.monogamous_polyamorous}
                content={noteContent.monogamous_polyamorous || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.religion}
                fieldId="religion"
                addendums={fieldAddendums?.religion}
                content={displayOptionValue(noteContent.religion)}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.religion_details}
                fieldId="religion_details"
                addendums={fieldAddendums?.religion_details}
                content={noteContent.religion_details || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.race}
                fieldId="race"
                addendums={fieldAddendums?.race}
                content={displayOptionValue(noteContent.race)}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.ethnicity}
                fieldId="ethnicity"
                addendums={fieldAddendums?.ethnicity}
                content={noteContent.ethnicity || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.education}
                fieldId="education"
                addendums={fieldAddendums?.education}
                content={displayOptionValue(noteContent.education)}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.current_employment}
                fieldId="current_employment"
                addendums={fieldAddendums?.current_employment}
                content={noteContent.current_employment || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.relationship}
                fieldId="relationship"
                addendums={fieldAddendums?.relationship}
                content={displayOptionValue(noteContent.relationship)}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.relationship_details}
                fieldId="relationship_details"
                addendums={fieldAddendums?.relationship_details}
                content={noteContent.relationship_details || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.emergency_contact_information
                }
                fieldId="emergency_contact_information"
                addendums={fieldAddendums?.emergency_contact_information}
                content={noteContent.emergency_contact_information || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.names_and_relations_of_others_present_in_the_interview
                }
                fieldId="names_and_relations_of_others_present_in_the_interview"
                addendums={
                    fieldAddendums?.names_and_relations_of_others_present_in_the_interview
                }
                content={
                    noteContent.names_and_relations_of_others_present_in_the_interview ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.chief_complaint}
                fieldId="chief_complaint"
                addendums={fieldAddendums?.chief_complaint}
                content={noteContent.chief_complaint || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.history_of_present_illness}
                fieldId="history_of_present_illness"
                addendums={fieldAddendums?.history_of_present_illness}
                content={noteContent.history_of_present_illness || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.past_psychiatric_history}
                fieldId="past_psychiatric_history"
                addendums={fieldAddendums?.past_psychiatric_history}
                content={noteContent.past_psychiatric_history || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.past_medical_history}
                fieldId="past_medical_history"
                addendums={fieldAddendums?.past_medical_history}
                content={noteContent.past_medical_history || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.past_psychiatric_hospitalization
                }
                fieldId="past_psychiatric_hospitalization"
                addendums={fieldAddendums?.past_psychiatric_hospitalization}
                content={noteContent.past_psychiatric_hospitalization || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.current_medications}
                fieldId="current_medications"
                addendums={fieldAddendums?.current_medications}
                content={noteContent.current_medications || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.allergies}
                fieldId="allergies"
                addendums={fieldAddendums?.allergies}
                content={noteContent.allergies || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.social_history}
                fieldId="social_history"
                addendums={fieldAddendums?.social_history}
                content={noteContent.social_history || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.family_history}
                fieldId="family_history"
                addendums={fieldAddendums?.family_history}
                content={noteContent.family_history || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.trauma_history}
                fieldId="trauma_history"
                addendums={fieldAddendums?.trauma_history}
                content={noteContent.trauma_history || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.lifestyle_excercise_diet_sleep
                }
                fieldId="lifestyle_excercise_diet_sleep"
                addendums={fieldAddendums?.lifestyle_excercise_diet_sleep}
                content={noteContent.lifestyle_excercise_diet_sleep || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.appearance}
                fieldId="appearance"
                addendums={fieldAddendums?.appearance}
                content={noteContent.appearance || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.movement}
                fieldId="movement"
                addendums={fieldAddendums?.movement}
                content={noteContent.movement || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.speech_orientation}
                fieldId="speech_orientation"
                addendums={fieldAddendums?.speech_orientation}
                content={noteContent.speech_orientation || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.affect_mood}
                fieldId="affect_mood"
                addendums={fieldAddendums?.affect_mood}
                content={noteContent.affect_mood || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.attention_concentration_cognition
                }
                fieldId="attention_concentration_cognition"
                addendums={fieldAddendums?.attention_concentration_cognition}
                content={noteContent.attention_concentration_cognition || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.overall_presentation}
                fieldId="overall_presentation"
                addendums={fieldAddendums?.overall_presentation}
                content={noteContent.overall_presentation || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.patient_ability_and_capability_to_respond_to_treatment
                }
                fieldId="patient_ability_and_capability_to_respond_to_treatment"
                addendums={
                    fieldAddendums?.patient_ability_and_capability_to_respond_to_treatment
                }
                content={displayCheckedOptions(
                    noteContent.patient_ability_and_capability_to_respond_to_treatment
                )}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                }
                fieldId="diminished_poor_or_limited_quality_patient_oriented_evidence_details"
                addendums={
                    fieldAddendums?.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                }
                content={
                    noteContent.diminished_poor_or_limited_quality_patient_oriented_evidence_details ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.ros}
                fieldId="ros"
                addendums={fieldAddendums?.ros}
                content={noteContent.ros || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.current_suicidal_ideation}
                fieldId="current_suicidal_ideation"
                addendums={fieldAddendums?.current_suicidal_ideation}
                content={noteContent.current_suicidal_ideation || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.current_homicidal_ideation}
                fieldId="current_homicidal_ideation"
                addendums={fieldAddendums?.current_homicidal_ideation}
                content={noteContent.current_homicidal_ideation || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.reported_violent_behavior}
                fieldId="reported_violent_behavior"
                addendums={fieldAddendums?.reported_violent_behavior}
                content={noteContent.reported_violent_behavior || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.inappropriate_illegal_behaviors
                }
                fieldId="inappropriate_illegal_behaviors"
                addendums={fieldAddendums?.inappropriate_illegal_behaviors}
                content={noteContent.inappropriate_illegal_behaviors || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.history_of_violence_to_self}
                fieldId="history_of_violence_to_self"
                addendums={fieldAddendums?.history_of_violence_to_self}
                content={noteContent.history_of_violence_to_self || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.history_of_violence_to_others
                }
                fieldId="history_of_violence_to_others"
                addendums={fieldAddendums?.history_of_violence_to_others}
                content={noteContent.history_of_violence_to_others || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.auditory_hallucinations}
                fieldId="auditory_hallucinations"
                addendums={fieldAddendums?.auditory_hallucinations}
                content={noteContent.auditory_hallucinations || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.visual_hallucinations}
                fieldId="visual_hallucinations"
                addendums={fieldAddendums?.visual_hallucinations}
                content={noteContent.visual_hallucinations || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.other_pertinent_information}
                fieldId="other_pertinent_information"
                addendums={fieldAddendums?.other_pertinent_information}
                content={noteContent.other_pertinent_information || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.lab_test_data_review}
                fieldId="lab_test_data_review"
                addendums={fieldAddendums?.lab_test_data_review}
                content={noteContent.lab_test_data_review || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.psychiatric_screening_measure_results
                }
                fieldId="psychiatric_screening_measure_results"
                addendums={
                    fieldAddendums?.psychiatric_screening_measure_results
                }
                content={
                    noteContent.psychiatric_screening_measure_results || "--"
                }
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.note}
                fieldId="note"
                addendums={fieldAddendums?.note}
                content={noteContent.note || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.plan}
                fieldId="plan"
                addendums={fieldAddendums?.plan}
                content={noteContent.plan || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.frequency_of_sessions}
                fieldId="frequency_of_sessions"
                addendums={fieldAddendums?.frequency_of_sessions}
                content={displayCheckedOptions(
                    noteContent.frequency_of_sessions
                )}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.other_frequency_of_sessions}
                fieldId="other_frequency_of_sessions"
                addendums={fieldAddendums?.other_frequency_of_sessions}
                content={noteContent.other_frequency_of_sessions || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.date_of_next_session}
                fieldId="date_of_next_session"
                addendums={fieldAddendums?.date_of_next_session}
                content={dateOfNextSession}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.prognosis}
                fieldId="prognosis"
                addendums={fieldAddendums?.prognosis}
                content={noteContent.prognosis || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.education_including_health_promotion_maintenance_and_psychosocial_needs
                }
                fieldId="education_including_health_promotion_maintenance_and_psychosocial_needs"
                addendums={
                    fieldAddendums?.education_including_health_promotion_maintenance_and_psychosocial_needs
                }
                content={
                    noteContent.education_including_health_promotion_maintenance_and_psychosocial_needs ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.lab_work_ordered}
                fieldId="lab_work_ordered"
                addendums={fieldAddendums?.lab_work_ordered}
                content={noteContent.lab_work_ordered || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.referrals_and_recommendations
                }
                fieldId="referrals_and_recommendations"
                addendums={fieldAddendums?.referrals_and_recommendations}
                content={noteContent.referrals_and_recommendations || "--"}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.overall_goals_of_care_frequency_and_duration_of_visits
                }
                fieldId="overall_goals_of_care_frequency_and_duration_of_visits"
                addendums={
                    fieldAddendums?.overall_goals_of_care_frequency_and_duration_of_visits
                }
                content={
                    noteContent.overall_goals_of_care_frequency_and_duration_of_visits ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.treatment_goal_one}
                fieldId="treatment_goal_one"
                addendums={fieldAddendums?.treatment_goal_one}
                content={noteContent.treatment_goal_one || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.treatment_goal_two}
                fieldId="treatment_goal_two"
                addendums={fieldAddendums?.treatment_goal_two}
                content={noteContent.treatment_goal_two || "--"}
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.treatment_goal_three}
                fieldId="treatment_goal_three"
                addendums={fieldAddendums?.treatment_goal_three}
                content={noteContent.treatment_goal_three || "--"}
            />

            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.modality}
                fieldId="modality"
                addendums={fieldAddendums?.modality}
                content={displayEnumValue(noteContent.modality)}
            />

            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.communication_factors_impacting_treatment
                }
                fieldId="communication_factors_impacting_treatment"
                addendums={
                    fieldAddendums?.communication_factors_impacting_treatment
                }
                content={displayCheckedOptions(
                    noteContent.communication_factors_impacting_treatment
                )}
            />
            <NoteContentItem
                headerText={
                    INTAKE_NOTE_MED_LABELS.details_around_communication_factors_impacting_treatment
                }
                fieldId="details_around_communication_factors_impacting_treatment"
                addendums={
                    fieldAddendums?.details_around_communication_factors_impacting_treatment
                }
                content={
                    noteContent.details_around_communication_factors_impacting_treatment ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={INTAKE_NOTE_MED_LABELS.files}
                fieldId="files"
                addendums={fieldAddendums?.files}
                content={
                    <div>
                        {data?.data?.note_content_documents &&
                        data?.data?.note_content_documents.length ? (
                            <div className="flex gap-[var(--spacing-items)] flex-wrap">
                                {data?.data?.note_content_documents.map(
                                    (doc) => (
                                        <ActionButton
                                            key={doc.note_content_document_id}
                                            variant="can-hover"
                                            size="auto"
                                            className="h-7 !text-text text-caption underline"
                                            actionTitle="View file"
                                            onClick={() => {
                                                setDocInView(doc);
                                            }}
                                        >
                                            <span>{`${displayNameInRightFormat(
                                                {
                                                    firstName:
                                                        doc.uploaded_by
                                                            .first_name,
                                                    lastName:
                                                        doc.uploaded_by
                                                            .last_name,
                                                },
                                                false
                                            )} -  ${doc.document_name}`}</span>
                                        </ActionButton>
                                    )
                                )}
                            </div>
                        ) : (
                            "--"
                        )}
                    </div>
                }
            />
        </>
    );
}
